import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ChevronRight } from "lucide-react";

/**
 * LatestEventsSlider — a compact auto-rotating hero strip that surfaces the
 * 5 newest upcoming events. Sits above the main <Hero /> on the homepage
 * so first-time visitors immediately see live inventory without scrolling.
 *
 * Behaviour:
 *  • Auto-advances every 4.5s; pauses on hover.
 *  • Dot pager + swipe-through via arrow.
 *  • Falls back to null (no space taken) if we don't have ≥1 event with a banner.
 */
export default function LatestEventsSlider({ events }) {
  const slides = useMemo(() => {
    const isUpcoming = (e) => {
      if (!e?.date) return true;
      try {
        const d = e.end_date || e.date;
        const dt = new Date(d.includes("T") ? d : `${d}T23:59:59`);
        return dt >= new Date();
      } catch { return true; }
    };
    return (events || [])
      .filter((e) => e.banner && isUpcoming(e))
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 5);
  }, [events]);

  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);
  const paused = useRef(false);

  useEffect(() => {
    if (slides.length < 2) return;
    timerRef.current = setInterval(() => {
      if (!paused.current) setIdx((i) => (i + 1) % slides.length);
    }, 4500);
    return () => clearInterval(timerRef.current);
  }, [slides.length]);

  if (slides.length === 0) return null;
  const cur = slides[idx];
  const dateStr = (() => {
    try {
      return cur?.date ? new Date(cur.date).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" }) : "";
    } catch { return ""; }
  })();

  return (
    <section
      data-testid="home-latest-slider"
      data-reveal="1"
      className="relative mx-auto max-w-7xl px-4 sm:px-6 pt-3"
      onMouseEnter={() => { paused.current = true; }}
      onMouseLeave={() => { paused.current = false; }}
    >
      <Link
        to={cur.slug ? `/e/${cur.slug}` : `/events/${cur.id}`}
        data-testid={`home-latest-slide-${idx}`}
        className="relative block overflow-hidden rounded-3xl border border-white/10 group card-mag"
        style={{ aspectRatio: "16/6" }}
      >
        {/* Cross-fade backdrop */}
        {slides.map((s, i) => (
          <img
            key={s.id}
            src={s.banner}
            alt={s.title}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}
            loading={i === 0 ? "eager" : "lazy"}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />

        {/* Foreground content */}
        <div className="relative h-full flex flex-col justify-end p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-lime text-ink text-[10px] font-mono font-bold uppercase tracking-widest">
              <span className="w-1.5 h-1.5 bg-ink rounded-full animate-pulse" /> Latest
            </span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-white/85">{dateStr}</span>
            {cur.city && (
              <span className="text-[10px] font-mono uppercase tracking-widest text-white/70 inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {cur.city}
              </span>
            )}
          </div>
          <div className="font-display text-2xl sm:text-4xl lg:text-5xl uppercase leading-[0.95] text-white line-clamp-2 max-w-3xl">
            {cur.title}
          </div>
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="text-[11px] font-mono uppercase tracking-widest text-white/70 hidden sm:block">
              {slides.length} live drops · tap for details
            </div>
            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur text-white text-[10px] font-mono font-bold uppercase tracking-widest group-hover:bg-white/25 transition-colors">
              View <ChevronRight className="w-3 h-3" />
            </span>
          </div>
        </div>

        {/* Dot pager */}
        {slides.length > 1 && (
          <div className="absolute top-3 right-3 sm:top-4 sm:right-4 flex gap-1.5" data-testid="home-latest-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                data-testid={`home-latest-dot-${i}`}
                onClick={(e) => { e.preventDefault(); setIdx(i); }}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-lime" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
              />
            ))}
          </div>
        )}
      </Link>
    </section>
  );
}
